import {Elastic, TimelineLite, TweenLite} from "gsap";
import {IMentalGameData} from "../server/MentalData";
import {Mental} from "../Mental";
import {MentalReelAreaView} from "../reel/MentalReelAreaView";
import {XFeaturePresentation} from "../winPresentations/xFeature/XFeaturePresentation";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MentalHelper} from "../utils/MentalHelper";
import {ReelEnhancer} from "../server/ReelEnhancerData";
import {FeaturePresentationHelper} from "../utils/FeaturePresentationHelper";
import {SoundManager} from "../sound/SoundManager";
import {MentalSoundConfig} from "../MentalSoundConfig";

export class MentalS2XWaysAnim extends PIXI.Container {
    private readonly _reelId: number;
    private readonly _reelArea: MentalReelAreaView;
    private _callBackFn: Function;
    private readonly _defSymWidth: number;
    private _defSymHeight: number;
    private _expandTime: number;

    private _symId: number;
    private _totalSymCount: number;
    private _resizedSymHeight: number;
    private _animTL: TimelineLite = new TimelineLite();
    private _gameData: IMentalGameData;

    private _xWaysBg: PIXI.Sprite;
    private _xWaysBorders: PIXI.Sprite[] = [];
    private _flashAnim: TimelineSprite;
    private _logoAnims: PIXI.spine.Spine[] = [];
    private _logoDivider: PIXI.Sprite[] = [];
    private _symbolPosIndex: number = 0;
    private _symbolSegmenHeight: number = 0;
    private _currentSymbolSize: number = 0;
    private _posY: number = 0;
    private _logoHeight: number = 99;

    constructor(reelId: number, callBackFn: Function, symId: number = 0) {
        super();

        this._reelId = reelId;
        this._reelArea = Mental.reelArea;
        this._callBackFn = callBackFn;
        this._defSymWidth = 144;
        this._defSymHeight = 198;
        this._expandTime = 0.1;
        this._symId = symId;

        this.init();
    }

    /**
     * starting point of xWays anim for S2 only
     */
    public playOpeningAnimationS2(symId: number, index: number): TimelineLite {
        this._gameData = Mental.model.gameData;
        this._symbolPosIndex = index;
        this._symId = symId;
        this._totalSymCount = 0;

        this._animTL = new TimelineLite();
        this._animTL.add(this.showXWaysAnim(symId));
        return this._animTL;
    }

    /**
     * starting point of expanding animation of S2
     * @param symId
     * @param index
     */
    public playExpandAnimationS2(symId: number, index: number): TimelineLite {
        this._gameData = Mental.model.gameData;
        this._symbolPosIndex = index;
        this._animTL = new TimelineLite();
        this._animTL.add(this.expandBackground());
        return this._animTL;
    }

    public hideXWaysOverlayAnim(): TimelineLite {
        this._gameData = Mental.model.gameData;

        const tl: TimelineLite = new TimelineLite();
        tl.add(this.hideXWaysAnim());
        tl.add(this.xWaysTransform());
        return tl;
    }

    public getBGCenterYPos(fromEnhancer: boolean): number {
        const logoItemIdx: number = (fromEnhancer ? 0 : (this._logoAnims.length === 2 ? 1 : 0));

        return (this.parent.y + this.y + this._logoAnims[logoItemIdx].y);
    }

    public getBGHeight(): number {
        return this._xWaysBg.height;
    }

    public resizeS2xWaysBg(isOnEnhancer: boolean = false, onXSplit: boolean = false, specialE2ClosedCase: boolean = false): TimelineLite {
        if (!this.visible) {
            return new TimelineLite;
        }
        if (!onXSplit) {
            onXSplit = specialE2ClosedCase;
        }
        if (onXSplit) {
            this._currentSymbolSize += 3;
            if (this._reelId === 1) {
                Mental.s2xWaysFeature._numberOfXWaysPlayedOnReel1[this._symbolPosIndex] = this._currentSymbolSize / 3;
            } else {
                Mental.s2xWaysFeature._numberOfXWaysPlayedOnReel3[this._symbolPosIndex] = this._currentSymbolSize / 3;

            }
            Mental.xWaysCounter.updateXwaysCounterFromS2Xways(this._reelId)
        }
        let symCount: number = 0;
        const featureTriggered: string[] = this._reelId == 1 ? this._gameData.firstS2FeatureTriggered : this._gameData.secondS2FeatureTriggered;
        for (let i: number = 0; i < this._symbolPosIndex; i++) {
            if (featureTriggered[i] == ReelEnhancer.XWAYS) {
                if (this._reelId == 1) {
                    symCount += Mental.s2xWaysFeature._numberOfXWaysPlayedOnReel1[i] * 3;
                } else {
                    symCount += Mental.s2xWaysFeature._numberOfXWaysPlayedOnReel3[i] * 3;
                }
            } else if ((featureTriggered[i] === ReelEnhancer.XSPLIT) || (this._symbolPosIndex === 1 && (featureTriggered[1] === ReelEnhancer.XWAYS))) { // modified for #421 => if there are 2 symbols(even resized, absolute counting of symbols i mean) visible above the actual xWays
                symCount += 2;
            } else {
                if (this._reelId === 1 && i === 0) { // if any normal symbol on top of xways and get split by xSplit on reel 3
                    const symbolSize: number = this._gameData.currentSymbolSize[this._reelId][this._symId];
                    symCount += symbolSize - this._currentSymbolSize;
                } else {
                    symCount += 1;
                }
            }
        }
        if (FeaturePresentationHelper._symbolHeightOnReel[this._reelId] !== -1) {
            let totalSymbolCount: number;

            if (isOnEnhancer) {
                totalSymbolCount = this._gameData.currentSymbolSize[this._reelId][0] + this._gameData.currentSymbolSize[this._reelId][1] +
                    this._gameData.currentSymbolSize[this._reelId][2];
                this._resizedSymHeight = (this._defSymHeight * 2.5) / totalSymbolCount;
            } else {
                totalSymbolCount = this._gameData.currentSymbolSize[this._reelId][0] + this._gameData.currentSymbolSize[this._reelId][1];
                this._resizedSymHeight = (this._defSymHeight * 2) / totalSymbolCount;
            }
        }
        const tl: TimelineLite = new TimelineLite(),
            prevSymSize: number = (this._symId === 1) ? this._gameData.currentSymbolSize[this._reelId][0] : 0,
            posY: number = this._resizedSymHeight * (prevSymSize + symCount),
            logoPosMult: number = 1,
            currSymSize: number = this._currentSymbolSize;
        let currSymHeight: number = this._resizedSymHeight * currSymSize;

        tl.add(() => {
            TweenLite.to(this.position, this._expandTime, {y: posY, ease: Elastic.easeOut.config(1, 0.95)});
        });

        tl.add(() => {
            TweenLite.to([this._xWaysBg, this._xWaysBorders[0]], this._expandTime, {
                height: currSymHeight,
                ease: Elastic.easeOut.config(1, 0.95)
            });

            TweenLite.to(this._flashAnim, this._expandTime, {
                height: currSymHeight * 1.4,
                y: (currSymHeight / 2) * logoPosMult,
                ease: Elastic.easeOut.config(1, 0.95)
            });
        });

        if (onXSplit) {
            this.createLogoAnims();
        }
        tl.add(() => {
            const availableHeight: number = (this._resizedSymHeight * 3);

            if (this._reelId === 1 || specialE2ClosedCase) {
                for (let i: number = 0; i < this._logoDivider.length; i++) {
                    this._logoDivider[i].visible = true;
                    TweenLite.to(this._logoDivider[i].position, {
                        duration: this._expandTime,
                        y: this._resizedSymHeight * ((i + 1) * 3),
                    });
                }
            }

            for (let i: number = 0; i < this._logoAnims.length; i++) {
                this._logoAnims[i].visible = true;
                this._logoAnims[i].state.addAnimation(0, "animation", false, 0);
                this._logoAnims[i].state.tracks[0].trackTime = 1;
                this._logoAnims[i].update(1);

                if (this._reelId === 1 || specialE2ClosedCase) {
                    TweenLite.to(this._logoAnims[i].scale, this._expandTime, {
                        y: Math.min(availableHeight / (this._logoHeight * 1.2), 1),
                        x: Math.min(availableHeight / (this._logoHeight * 1.2), 1),
                        onComplete: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = this._resizedSymHeight * (1.5 + (i * 3));
                                if (i === 0 && this._logoAnims.length > 1) {
                                    this._logoAnims[i].position.y += 5;
                                }
                            }
                        },
                        onUpdate: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = this._resizedSymHeight * (1.5 + (i * 3));
                                if (i === 0 && this._logoAnims.length > 1) {
                                    this._logoAnims[i].position.y += 5;
                                }
                            }
                        }

                    });
                } else {
                    TweenLite.to(this._logoAnims[i].scale, this._expandTime / 2, {
                        y: Math.min((this._resizedSymHeight * 3 / (this._logoHeight * 1.2)), 1),
                        x: Math.min((this._resizedSymHeight * 3 / (this._logoHeight * 1.2)), 1),
                        onComplete: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = (currSymHeight / 2) + 5;
                            }
                        }, onUpdate: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = (currSymHeight / 2) + 5;
                            }
                        }
                    });
                }
            }
        });

        return tl;
    }

    public doTint(tint: number) : void {
        this._xWaysBg.tint = tint;
        this._xWaysBorders.forEach((border: PIXI.Sprite) => {
            border.tint = tint;
        });
        this._logoDivider.forEach((divider: PIXI.Sprite) => {
            divider.tint = tint;
        });
        this._logoAnims.forEach((logo: PIXI.spine.Spine) => {
            logo.tint = tint;
        });
    }

    private init(): void {
        this.reset();
        this.visible = false;
        this._xWaysBg = this.createBackground();
        this._xWaysBorders = this.createBorders();
        this._flashAnim = this.createFlash();

        this.addChild(this._xWaysBg, this._xWaysBorders[0], this._xWaysBorders[1], this._flashAnim);
        this.resetGraphics();
    }

    private createLogoAnims(): void {
        const logoCount: number = this._currentSymbolSize / 3;

        if (this._logoAnims.length) {
            this.removeLogoSpines();
        }
        for (let i: number = 0; i < logoCount; i++) {
            const logoSpine: PIXI.spine.Spine = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("Xways"));
            logoSpine.name = "logoSpine_" + i;
            logoSpine.visible = false;
            this._logoAnims.push(logoSpine);
            this.addChildAt(logoSpine, this.children.length - 1);
        }

        for (let i = 0; i < logoCount - 1; i++) {
            const logoSprite: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysDivider")[0]);
            logoSprite.visible = false;
            logoSprite.anchor.set(0.5);
            this._logoDivider.push(logoSprite);
            this.addChildAt(logoSprite, this.children.length - 1);
        }
    }

    private createBorders(): PIXI.Sprite[] {
        const borders: PIXI.Sprite[] = [],
            topBorder: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysTopFrame")[0]),
            bottomBorder: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysBottomFrame")[0]),
            scaleY: number = 1;

        topBorder.anchor.set(0.5, 0);
        topBorder.scale.set(1, scaleY);
        topBorder.name = "topBorder";

        bottomBorder.anchor.set(0.5, 0);
        bottomBorder.scale.set(1, scaleY);
        bottomBorder.name = "bottomBorder";

        borders.push(topBorder, bottomBorder);

        return borders;
    }

    private createFlash(): TimelineSprite {
        const flashAnim: TimelineSprite = MentalHelper.createSprite("flashShine");

        flashAnim.anchor.set(0.5);
        flashAnim.position.set(0);
        flashAnim.scale.set(1.7, 1);
        flashAnim.name = "flashAnim";
        flashAnim.blendMode = PIXI.BLEND_MODES.ADD;
        flashAnim.hide();

        return flashAnim;
    }

    private createBackground(): PIXI.Sprite {
        const bg: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysBackground")[0]),
            anchorY: number = 0;
        bg.anchor.set(0.5, anchorY);
        bg.position.set(0);
        bg.height = this._defSymHeight;
        bg.name = "xWaysBg";

        return bg;
    }

    private setBasics(symId: number): void {
        this._currentSymbolSize = 3;
        const featureTriggered: string[] = (this._reelId == 1) ? this._gameData.firstS2FeatureTriggered : this._gameData.secondS2FeatureTriggered;
        const featureSymbolNames: string[] = (this._reelId == 1) ? this._gameData.firstS2SymbolName : this._gameData.secondS2SymbolName;

        let availableHeight: number;

        if (featureTriggered[this._symbolPosIndex] === ReelEnhancer.XWAYS) {
            featureSymbolNames.splice(featureSymbolNames.indexOf("S2"), 1, this._gameData.mysterySymbols[this._reelId][this._symId],
                this._gameData.mysterySymbols[this._reelId][this._symId], this._gameData.mysterySymbols[this._reelId][this._symId]);
        }

        this._gameData.currentSymbolSize[this._reelId][symId] = featureSymbolNames.length;
        for (let i = 0; i < this._gameData.currentSymbolSize[this._reelId].length - 1; i++) {
            this._totalSymCount += this._gameData.currentSymbolSize[this._reelId][i];
        }
        availableHeight = this._defSymHeight * 2;

        this._resizedSymHeight = (availableHeight / this._totalSymCount);

        this.createLogoAnims();
        this.resetGraphics();
    }

    private repositionXWays(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            this._logoAnims.forEach((item: PIXI.spine.Spine) => {
                item.visible = true;
                item.scale.set(Math.min(this._symbolSegmenHeight / this._logoHeight, 1));
                item.position.y = ((this._symbolSegmenHeight / 2));
                item.state.setAnimation(0, "animation", false);
            });
        });

        tl.add(TweenLite.to([this._xWaysBg, this._xWaysBorders[0]], {
            duration: 0.01,
            height: this._symbolSegmenHeight
        }));
        tl.add(TweenLite.to(this.position, 0.01, {
            y: this._posY,
            onComplete: () => {
                this.visible = true;
            }
        }));

        return tl;
    }

    private showXWaysAnim(symId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        this._posY = 0;
        this._symbolSegmenHeight = 0;
        const resizedHeight: number = FeaturePresentationHelper._symbolHeightOnReel[this._reelId];

        let symCount: number = 0;
        for (let i = 0; i < this._symbolPosIndex; i++) {
            const featureTriggered: string[] = this._reelId == 1 ? this._gameData.firstS2FeatureTriggered : this._gameData.secondS2FeatureTriggered;
            if (featureTriggered[i] == ReelEnhancer.XWAYS) {
                symCount += 3;
            } else if (featureTriggered[i] === ReelEnhancer.XSPLIT) {
                symCount += 2;
            } else {
                symCount += 1;
            }
        }
        if (resizedHeight !== -1) { //  when reel is resized already
            if (this._symId === 1) {
                symCount += this._gameData.fireFrameSymbolSize[this._reelId][0];
            }
            this._posY = resizedHeight * symCount;
            this._symbolSegmenHeight = resizedHeight;
        } else { // when reel is not resized
            this._posY += (this._symId === 1) ? 198 : 0;
            this._symbolSegmenHeight = (198 / this._gameData.currentSymbolSize[this._reelId][this._symId]);
            this._posY += this._symbolSegmenHeight * symCount;
        }
        tl.add(this.repositionXWays());
        this.setBasics(symId);

        /**
         * xWaysPlayed played values updated from here
         */
        const index: number = this._reelId === 1 ? 0 : 1;
        this._gameData.xWaysPlayed[index] = true;

        return tl;
    }

    /**
     *  only calling this function for enhancer as of now.
     * @private
     */
    private resizeReel(): void {
        FeaturePresentationHelper.resizeSymbolWithInReel(this._reelId, false);
    }

    private expandBackground(): TimelineLite {
        let symCount: number = 0;
        for (let i = 0; i < this._symbolPosIndex; i++) {
            const featureTriggered: string[] = this._reelId === 1 ? this._gameData.firstS2FeatureTriggered : this._gameData.secondS2FeatureTriggered;
            if (featureTriggered[i] === ReelEnhancer.XWAYS) {
                symCount += 3;
            } else if (featureTriggered[i] === ReelEnhancer.XSPLIT) {
                symCount += 2;
            } else {
                symCount += 1;
            }
        }
        if (FeaturePresentationHelper._symbolHeightOnReel[this._reelId] !== -1) {
            const totalSymbolCount: number = this._gameData.currentSymbolSize[this._reelId][0] + this._gameData.currentSymbolSize[this._reelId][1];
            this._resizedSymHeight = (this._defSymHeight * 2) / totalSymbolCount;
        }
        const tl: TimelineLite = new TimelineLite(),
            prevSymSize: number = (this._symId === 1) ? this._gameData.currentSymbolSize[this._reelId][0] : 0,
            posY: number = this._resizedSymHeight * (prevSymSize + symCount),
            currSymSize: number = this._currentSymbolSize,
            currSymHeight: number = this._resizedSymHeight * currSymSize,
            logoPosMult: number = 1;

        // #203 -> Viktor mentioned on Skype to use this.
        tl.add(() => SoundManager.playSoundEffect(MentalSoundConfig.NUDGE), 0);

        tl.add(() => {
            TweenLite.to(this.position, this._expandTime, {y: posY, ease: Elastic.easeOut.config(1, 0.95)});
        });

        tl.add(() => {
            TweenLite.to([this._xWaysBg, this._xWaysBorders[0]], this._expandTime, {
                height: currSymHeight,
                ease: Elastic.easeOut.config(1, 0.95)
            });

            TweenLite.to(this._flashAnim, this._expandTime, {
                height: currSymHeight * 1.4,
                y: (currSymHeight / 2) * logoPosMult,
                ease: Elastic.easeOut.config(1, 0.95)
            });
            for (let i: number = 0; i < this._logoAnims.length; i++) {
                if (this._reelId === 1) {
                    TweenLite.to(this._logoAnims[i].scale, this._expandTime, {
                        y: Math.min(currSymHeight / this._logoHeight, 1),
                        x: Math.min(currSymHeight / this._logoHeight, 1),
                        onComplete: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = (currSymHeight / 2) + 5;
                            }
                        }, onUpdate: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = (currSymHeight / 2) + 5;
                            }
                        }
                    });
                } else {
                    TweenLite.to(this._logoAnims[i].scale, this._expandTime / 2, {
                        y: Math.min((this._resizedSymHeight * 3 / (this._logoHeight * 1.2)), 1),
                        x: Math.min((this._resizedSymHeight * 3 / (this._logoHeight * 1.2)), 1),
                        onComplete: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = (currSymHeight / 2) + 5;
                            }
                        }, onUpdate: () => {
                            if (this._logoAnims.length && this._logoAnims[i]) { // to fix #344
                                this._logoAnims[i].position.y = (currSymHeight / 2) + 5;
                            }
                        }
                    });
                }
            }
            let featureSymbolNames: string[] = (this._reelId === 1) ? this._gameData.firstS2SymbolName : this._gameData.secondS2SymbolName;
            XFeaturePresentation.playXWaysFeaturePresentation(this._reelId, this._symId, featureSymbolNames, featureSymbolNames.length, false);
            this.resizeReel();
        });
        return tl;
    }

    private resetGraphics(): void {
        this._xWaysBg.position.set(0);
        this._xWaysBg.height = this._defSymHeight;
        this._xWaysBorders[0].height = this._defSymHeight;
        this.position.x = this._reelArea.getReel(this._reelId).position.x;
        this._flashAnim.height = this._defSymHeight;
    }

    private hideXWaysAnim(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.add(TweenLite.to({}, {
            duration: 0.02,
            onComplete: () => this.reset()
        }), (this.visible ? 0.29 : 0));

        return tl;
    }

    private reset(): void {
        const time: number = (this._symId === 2) ? 0.2 : 0;
        TweenLite.to(this, {
            duration: time, alpha: 0, onComplete: () => {
                this.alpha = 1;
                this.removeLogoSpines();
            }
        });
    }

    private xWaysTransform(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        // #86
        if (this._gameData.betWayWins.length > 0) {
            tl.add(this._flashAnim.getAnimationAutoShowHide(true, true));
        }

        tl.add(() => {this.visible = false});

        return tl
    }

    private removeLogoSpines(): void {
        for (let i: number = 0; i < this._logoAnims.length; i++) {
            this._logoAnims[i].state.clearTracks();
            this.removeChild(this._logoAnims[i]);
        }
        for (let i: number = 0; i < this._logoDivider.length; i++) {
            this.removeChild(this._logoDivider[i]);
        }
        this._logoAnims = [];
        this._logoDivider = [];
    }
}